import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Card } from '../ui'
import type { ImpactReport } from '../../types'

interface Props {
  report: ImpactReport
  /** Marketing renders the same numbers at a tighter scale than the app page. */
  compact?: boolean
}

/**
 * The five headline numbers of an impact report. Extracted from ImpactReportPage
 * so the marketing page can show the real component fed with demo data.
 */
const ImpactStatsRow: React.FC<Props> = ({ report, compact }) => {
  const value = compact ? 'text-2xl' : 'text-3xl'

  return (
    <div className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 ${compact ? '' : 'mb-8'}`}>
      <Card elevated>
        <p className="text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="impact.totalRespondents" /></p>
        <p className={`${value} font-bold text-foreground`}>{report.total_respondents}</p>
      </Card>
      <Card elevated>
        <p className="text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="impact.multiAttempt" /></p>
        <p className={`${value} font-bold text-foreground`}>{report.respondents_with_multiple_attempts}</p>
        <p className="text-xs text-foreground-muted"><FormattedMessage id="impact.excluded" values={{ count: report.respondents_excluded }} /></p>
      </Card>
      <Card elevated>
        <p className="text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="impact.baselineAvg" /></p>
        <p className={`${value} font-bold text-foreground`}>
          {report.baseline_avg_score != null ? report.baseline_avg_score.toFixed(1) : '--'}
        </p>
      </Card>
      <Card elevated>
        <p className="text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="impact.latestAvg" /></p>
        <p className={`${value} font-bold text-foreground`}>
          {report.latest_avg_score != null ? report.latest_avg_score.toFixed(1) : '--'}
        </p>
      </Card>
      <Card elevated>
        <p className="text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="impact.impactDelta" /></p>
        <p className={`${value} font-bold ${
          report.overall_delta == null ? 'text-foreground-muted'
          : report.overall_delta >= 0 ? 'text-success' : 'text-error'
        }`}>
          {report.overall_delta != null
            ? `${report.overall_delta >= 0 ? '+' : ''}${report.overall_delta.toFixed(1)}`
            : '--'}
        </p>
        {report.overall_delta_percent != null && (
          <p className="text-xs text-foreground-muted">
            {report.overall_delta_percent >= 0 ? '+' : ''}{report.overall_delta_percent}%
          </p>
        )}
      </Card>
    </div>
  )
}

export default ImpactStatsRow
